import React, { useContext, useEffect, useState, useRef } from 'react';
import styled, { css } from 'styled-components';
import { shallow } from 'zustand/shallow';

import ChevronDown from '../common/icon/ChevronDown';
import Global from '../common/icon/Global';

import { LayoutContext } from '../../context/LayoutProvider';
import useLanguage from '../../store/language';

const SelectOptions = styled.ul`
  transition: height 0.3s ease 0s;
  position: absolute;
  z-index: 9999;
  list-style: none;
  left: 50%;
  width: 118px;
  overflow-y: none;
  padding: 0;
  border-radius: 6px;
  box-shadow: 0 8px 20px 0 rgba(0, 0, 0, 0.25);
  background-color: ${props => props.theme.palette['white']};
  color: ${props => props.theme.palette['black']};
  top: calc(100% + 0px);
  transform: translateX(-50%);
  text-align: center;
  font-weight: 500;
  margin-block-start: ${({ theme }) => theme.spacing(2)};
  margin-block-end: 0;
  @supports (-webkit-touch-callout: none) {
    top: ${props => `${props.theme.spacing(2)} !important`};
  }
  ${props =>
    props.isMobile
      ? css`
          font-size: ${props => props.theme.fontSize['12']};
        `
      : css`
          font-size: ${props => props.theme.fontSize['14']};
        `};
  ${props =>
    props['aria-expanded']
      ? css`
          &,
          & * {
            visibility: visible;
            height: auto;
          }
        `
      : css`
          &,
          & * {
            visibility: hidden;
            height: 0px;
          }
        `};
`;

const SelectOption = styled.li`
  padding: ${props => props.theme.spacing(2)};
`;

const SelectLabel = styled.label`
  display: flex;
  align-items: center;
  font-weight: bold;
  gap: ${({ theme }) => theme.spacing(1)};
  color: ${({ theme }) => theme.palette.white};
  ${props =>
    props.isMobile
      ? css`
          font-size: ${props => props.theme.fontSize['12']};
        `
      : css`
          font-size: ${props => props.theme.fontSize['14']};
        `};
`;

const ChevronIcon = styled(ChevronDown)`
  margin-right: ${props => props.theme.spacing(1)};
  transition: all 0.1s;
`;

const SelectContainer = styled.div`
  display: flex;
  position: relative;
  border-radius: 100px;
  box-sizing: border-box;
  background-color: transparent;
  align-self: center;
  justify-content: space-between;
  align-items: center;
  user-select: none;
  border-width: 1px;
  border-style: solid;
  cursor: pointer;
  & * {
    cursor: pointer;
  }
  ${props =>
    props.isMobile
      ? css`
          width: 120px;
          padding: ${props => props.theme.spacing(1)};
        `
      : css`
          width: 149px;
          padding: ${props => props.theme.spacing(2, 6)};
        `};
  ${props =>
    props['aria-pressed']
      ? css`
          border-color: ${({ theme }) => theme.palette.primary.main};
          & ${ChevronIcon} {
            transform: scale(0.8) rotate(180deg);
          }
        `
      : css`
          border-color: #888;
          & ${ChevronIcon} {
            transform: scale(0.8) rotate(0deg);
          }
        `};
`;

const SelectLanguage = () => {
  const { isMobile } = useContext(LayoutContext);

  const { language, languageList, setLanguage } = useLanguage(
    state => ({
      language: state.language,
      languageList: state.languageList,
      setLanguage: state.setLanguage,
    }),
    shallow
  );

  const [isClicked, setIsClicked] = useState(false);

  const wrapperRef = useRef(null);

  useEffect(() => {
    document.addEventListener('mousedown', handleDocClick, false);
    return () => {
      document.removeEventListener('mousedown', handleDocClick, false);
    };
  }, []);

  const handleDocClick = e => {
    if (!wrapperRef.current.contains(e.target)) setIsClicked(false);
  };

  const handleClose = () => {
    setIsClicked(prev => !prev);
  };

  const handleChange = _val => {
    setLanguage(_val);
    // setIsClicked(prev => !prev);
  };

  return (
    <SelectContainer aria-pressed={isClicked} onClick={handleClose} isMobile={isMobile} ref={wrapper => (wrapperRef.current = wrapper)}>
      <SelectLabel isMobile={isMobile}>
        <Global />
        {languageList[languageList.findIndex(l => l.value === language)]['value_en']}
      </SelectLabel>
      <ChevronIcon />
      <SelectOptions aria-expanded={isClicked} isMobile={isMobile}>
        {languageList
          .filter(i => i.value !== language)
          .map(l => (
            <SelectOption value={l.value} key={l.value} onClick={() => handleChange(l.value)} isMobile={isMobile}>
              {l[`value_en`]}
            </SelectOption>
          ))}
      </SelectOptions>
    </SelectContainer>
  );
};

export default SelectLanguage;
